export default function LegalTerms() {
  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="mx-auto max-w-4xl px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h1 className="text-3xl font-bold text-primary mb-4">Terms & Conditions</h1>
          <p className="text-sm text-secondary mb-8">
            Last Updated: {new Date().toLocaleDateString()}
          </p>

          <div className="prose prose-sm max-w-none space-y-6">
            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">1. Agreement to Terms</h2>
              <p className="text-secondary leading-relaxed">
                By accessing and using the Deal Velocity website and services (collectively, the "Services"), you agree to be bound by these Terms and Conditions ("Terms"). If you do not agree to these Terms, you may not use our Services.
              </p>
              <p className="text-secondary leading-relaxed mt-2">
                These Terms constitute a legally binding agreement between you and Benjamin & Associates Inc. (trading as "Deal Velocity"), a company registered in South Africa.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">2. Services Description</h2>

              <h3 className="text-lg font-medium text-primary mb-2">2.1 Template Products</h3>
              <p className="text-secondary leading-relaxed">
                We offer digital legal contract templates for South African businesses. Templates are provided in editable formats (Word, PDF) and are designed for general business use.
              </p>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">2.2 Contract Audit Services</h3>
              <p className="text-secondary leading-relaxed">
                We provide professional legal review and audit services for commercial contracts. Our audits include risk assessment, redline suggestions, and recommendations.
              </p>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">2.3 Lead Magnet</h3>
              <p className="text-secondary leading-relaxed">
                We may offer free resources, guides, or templates in exchange for your contact information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">3. User Accounts and Registration</h2>
              <p className="text-secondary leading-relaxed">
                To access certain Services, you may need to create an account. You agree to:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and update your information to keep it accurate</li>
                <li>Maintain the security of your password and account</li>
                <li>Accept all responsibility for activities under your account</li>
                <li>Notify us immediately of any unauthorized access</li>
              </ul>
              <p className="text-secondary leading-relaxed mt-2">
                We reserve the right to suspend or terminate accounts that violate these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">4. Purchases and Payment</h2>

              <h3 className="text-lg font-medium text-primary mb-2">4.1 Pricing</h3>
              <p className="text-secondary leading-relaxed">
                All prices are listed in South African Rand (ZAR) and include applicable VAT where required. We reserve the right to change prices at any time, but price changes will not affect orders already placed.
              </p>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">4.2 Payment Processing</h3>
              <p className="text-secondary leading-relaxed">
                Payments are processed securely through third-party payment processors (Paystack, PayFast). We do not store your full credit card information. By making a purchase, you agree to the payment processor's terms and conditions.
              </p>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">4.3 Order Confirmation</h3>
              <p className="text-secondary leading-relaxed">
                After successful payment, you will receive an order confirmation email with access to your purchased products or services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">5. Refund Policy</h2>

              <h3 className="text-lg font-medium text-primary mb-2">5.1 Template Products</h3>
              <p className="text-secondary leading-relaxed">
                Due to the digital nature of our template products, all sales are final once you have downloaded the files. We offer a 7-day money-back guarantee if:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>You have not downloaded the files</li>
                <li>There is a technical issue preventing access (and we cannot resolve it)</li>
                <li>The product is materially different from the description</li>
              </ul>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">5.2 Audit Services</h3>
              <p className="text-secondary leading-relaxed">
                Audit service fees are non-refundable once work has commenced. If you cancel before we begin review, you will receive a full refund minus payment processing fees.
              </p>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">5.3 Refund Requests</h3>
              <p className="text-secondary leading-relaxed">
                To request a refund, contact support@dealvelocity.co.za within the applicable period. Refunds are processed within 14 business days to your original payment method.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">6. License and Usage Rights</h2>

              <h3 className="text-lg font-medium text-primary mb-2">6.1 Template License</h3>
              <p className="text-secondary leading-relaxed">
                Upon purchase, you receive a non-exclusive, non-transferable license to use the templates for your business purposes. You may:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Edit and customize templates for your specific needs</li>
                <li>Use templates for multiple projects within your organization</li>
                <li>Print copies for internal use</li>
              </ul>
              <p className="text-secondary leading-relaxed mt-2">You may NOT:</p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Resell, redistribute, or share templates with third parties</li>
                <li>Claim templates as your own work</li>
                <li>Use templates for illegal or fraudulent purposes</li>
                <li>Remove copyright or attribution notices</li>
              </ul>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">6.2 Audit Reports</h3>
              <p className="text-secondary leading-relaxed">
                Audit reports are provided for your exclusive use and are subject to attorney-client privilege. You may not publish or distribute reports without our written consent.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">7. Disclaimers and Limitations</h2>

              <h3 className="text-lg font-medium text-primary mb-2">7.1 Not Legal Advice</h3>
              <div className="bg-yellow-50 border border-yellow-300 rounded-md p-4">
                <p className="text-secondary leading-relaxed font-semibold">
                  IMPORTANT: Our templates and audit services do not constitute legal advice. They are general resources and should be reviewed by a qualified attorney before use in your specific situation.
                </p>
              </div>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">7.2 No Attorney-Client Relationship</h3>
              <p className="text-secondary leading-relaxed">
                Purchasing our products or services does not create an attorney-client relationship unless we explicitly agree to represent you in a separate written agreement.
              </p>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">7.3 Suitability</h3>
              <p className="text-secondary leading-relaxed">
                You are responsible for determining whether our templates are suitable for your specific needs and comply with applicable laws and regulations.
              </p>

              <h3 className="text-lg font-medium text-primary mb-2 mt-4">7.4 Service Availability</h3>
              <p className="text-secondary leading-relaxed">
                We strive to maintain 24/7 availability but do not guarantee uninterrupted access. We may suspend Services for maintenance, updates, or force majeure events.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">8. Limitation of Liability</h2>
              <p className="text-secondary leading-relaxed">
                To the maximum extent permitted by South African law:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our total liability shall not exceed the amount you paid for the specific product or service</li>
                <li>We are not responsible for losses caused by your misuse of templates or failure to seek legal counsel</li>
                <li>We are not liable for third-party service failures (payment processors, hosting providers)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">9. Intellectual Property</h2>
              <p className="text-secondary leading-relaxed">
                All content on our website and in our products (text, graphics, logos, designs) is protected by copyright, trademark, and other intellectual property laws. Deal Velocity™ is a trademark of Benjamin & Associates Inc.
              </p>
              <p className="text-secondary leading-relaxed mt-2">
                You may not copy, reproduce, or create derivative works from our website content without written permission.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">10. User Conduct</h2>
              <p className="text-secondary leading-relaxed">You agree not to:</p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Attempt to hack, disrupt, or damage our Services</li>
                <li>Upload malicious code or viruses</li>
                <li>Impersonate others or provide false information</li>
                <li>Use automated tools to access our Services (bots, scrapers)</li>
                <li>Share account credentials with others</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">11. Privacy and Data Protection</h2>
              <p className="text-secondary leading-relaxed">
                Your use of our Services is subject to our Privacy Policy, which explains how we collect, use, and protect your personal information in compliance with POPIA.
              </p>
              <p className="text-secondary leading-relaxed mt-2">
                Please review our{' '}
                <a href="/privacy" className="text-blue-600 hover:underline">
                  Privacy Policy
                </a>{' '}
                for detailed information.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">12. Confidentiality</h2>
              <p className="text-secondary leading-relaxed">
                For audit services, we maintain strict confidentiality of all contracts and business information you submit. All materials are protected by attorney-client privilege and will not be disclosed to third parties without your consent, except as required by law.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">13. Termination</h2>
              <p className="text-secondary leading-relaxed">
                We may suspend or terminate your access to our Services at any time for violations of these Terms. Upon termination:
              </p>
              <ul className="list-disc pl-6 text-secondary space-y-1 mt-2">
                <li>Your right to use the Services immediately ceases</li>
                <li>You must stop using any templates or materials</li>
                <li>Provisions relating to intellectual property, liability, and dispute resolution survive termination</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">14. Modifications to Terms</h2>
              <p className="text-secondary leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our website and updating the "Last Updated" date. Your continued use after changes constitutes acceptance.
              </p>
              <p className="text-secondary leading-relaxed mt-2">
                If you do not agree to the modified Terms, you must stop using our Services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">15. Governing Law and Jurisdiction</h2>
              <p className="text-secondary leading-relaxed">
                These Terms are governed by the laws of the Republic of South Africa. Any disputes arising from these Terms or your use of our Services shall be subject to the exclusive jurisdiction of the courts of South Africa.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">16. Dispute Resolution</h2>
              <p className="text-secondary leading-relaxed">
                In the event of a dispute, you agree to first attempt informal resolution by contacting us at support@dealvelocity.co.za. If we cannot resolve the dispute within 30 days, either party may pursue formal legal proceedings.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">17. Severability</h2>
              <p className="text-secondary leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary, and the remaining provisions will remain in full force and effect.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">18. Entire Agreement</h2>
              <p className="text-secondary leading-relaxed">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and Deal Velocity regarding your use of our Services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-3">19. Contact Information</h2>
              <p className="text-secondary leading-relaxed">
                If you have questions about these Terms, please contact us:
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mt-2">
                <p className="text-secondary"><strong>Benjamin & Associates Inc.</strong></p>
                <p className="text-secondary">Trading as: Deal Velocity</p>
                <p className="text-secondary"><strong>Email:</strong> legal@dealvelocity.co.za</p>
                <p className="text-secondary"><strong>Support:</strong> support@dealvelocity.co.za</p>
                <p className="text-secondary"><strong>Website:</strong> www.dealvelocity.co.za</p>
              </div>
            </section>

            <div className="bg-gray-100 border-l-4 border-primary p-4 mt-8">
              <p className="text-sm text-secondary italic">
                By using Deal Velocity's services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
